import { useState, useCallback } from 'react';
import { apiService, BusinessRequest, UpdateBusinessRequest } from '../services/api';
import type { BusinessDetailsResponse } from '../types/api-responses';
import { getApiErrorMessage } from '../types/api-errors';

export const useBusiness = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createBusiness = useCallback(async (data: BusinessRequest): Promise<BusinessDetailsResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.createBusiness(data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'An error occurred');
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const checkBusinessExists = useCallback(async (): Promise<BusinessDetailsResponse | null> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getMyBusiness();
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
            const msg = getApiErrorMessage(err, 'An error occurred');
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateBusiness = useCallback(async (businessId: number, data: UpdateBusinessRequest): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.updateBusiness(businessId, data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'An error occurred');
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteBusiness = useCallback(async (businessId: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteBusiness(businessId);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'An error occurred');
            setError(msg);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        createBusiness,
        checkBusinessExists,
        updateBusiness,
        deleteBusiness,
        loading,
        error
    };
};
